import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { t } from '@insight/toolkit-utils';

/**
 * The threshold that must be exceeded for swipe/wheel actions to trigger page
 * transitions.
 */
const SWIPE_THRESHOLD = 65;

/**
 * A carousel allows multiple pieces of featured content to occupy an allocated
 * amount of space.
 */
export default class EnhancedCarousel extends Component {
  constructor(props) {
    super(props);
    this.slides = React.createRef();
    this.state = {
      page: 0,
      totalItems: React.Children.count(props.children), // Count total items
    };

    this.autoRotate = props.autoRotate;
    this.autoRotateTimeoutHandle = 0;
    this.autoRotateInterval = props.autoRotateInterval;

    this.isAnimating = false;
    this.touchStartX = null;
  }

  // Breakpoints for device types
  getDeviceType() {
    const width = window.innerWidth;
    if (width < 768) {
      return 'mobile'; // Mobile devices
    } else if (width >= 768 && width < 1024) {
      return 'tablet'; // Tablet devices
    } else {
      return 'desktop'; // Desktop devices
    }
  }

  // Touch event handlers
  onTouchStart = ({ touches }) => {
    this.touchStartX = touches[0].clientX;
  };

  onTouchMove = ({ touches }) => {
    const current = touches[0].clientX;

    if (this.touchStartX !== null && current !== null) {
      const delta = current - this.touchStartX;

      if (delta > SWIPE_THRESHOLD) {
        this.touchStartX = null;
        this.requestPreviousPage();
      } else if (delta < -SWIPE_THRESHOLD) {
        this.touchStartX = null;
        this.requestNextPage();
      }
    }
  };

  onTouchEnd = () => {
    this.touchStartX = null;
  };

  onWheel = (e) => {
    if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) {
      e.preventDefault();

      if (!this.isAnimating) {
        e.deltaX < -SWIPE_THRESHOLD && this.requestPreviousPage();
        e.deltaX > SWIPE_THRESHOLD && this.requestNextPage();
      }
    }
  };

  onTransitionEnd = (event) => {
    if (event.target === this.slides.current) {
      this.isAnimating = false;
    }
  };

  onMouseEnter = () => {
    this.cancelAutoRotate();
    window.addEventListener('wheel', this.onWheel, { passive: false });
  };

  onMouseLeave = () => {
    this.startAutoRotate();
    window.removeEventListener('wheel', this.onWheel);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.children.length !== this.props.children.length) {
      this.refreshPage();
    }
  }

  requestPage(requestedPage) {
    const { children } = this.props;
    const { page: currentPage } = this.state;
    const { scrollWidth, offsetWidth } = this.slides.current;

    const slideCount = children.length;
    const pageCount = Math.ceil(scrollWidth / offsetWidth - 0.01);
    const slidesPerPage = Math.round((offsetWidth * slideCount) / scrollWidth);

    const newPage = Math.min(pageCount - 1, Math.max(0, requestedPage));

    this.isAnimating = newPage !== currentPage;

    this.setState({
      page: newPage,
      pageCount,
      slidesPerPage,
    });
  }

  requestNextPage = () => {
    this.requestPage(this.state.page + 1);
    window.sessionStorage.setItem('carouselDirection', 'Right');
  };

  requestPreviousPage = () => {
    this.requestPage(this.state.page - 1);
    window.sessionStorage.setItem('carouselDirection', 'Left');
  };

  requestRotatePage = () => {
    this.requestPage((this.state.page + 1) % this.state.pageCount);
  };

  refreshPage = () => {
    this.requestPage(this.state.page);
  };

  onResize = () => {
    this.refreshPage();
  };

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    window.sessionStorage.setItem('carouselDirection', 'Start');
    if (this.autoRotate) {
      this.startAutoRotate();
    }
    this.refreshPage();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    this.cancelAutoRotate();
  }

  startAutoRotate() {
    let { pageCount } = this.props;
    if (!this.autoRotate || pageCount === 0 || !!this.autoRotateTimeoutHandle)
      return;
    const self = this;
    const autoRotateFunction = () => {
      let newPage = (self.state.page + 1) % self.state.pageCount;
      self.setState({
        page: newPage,
      });
      self.autoRotateTimeoutHandle = setTimeout(
        autoRotateFunction,
        self.autoRotateInterval
      );
    };
    self.autoRotateTimeoutHandle = setTimeout(
      autoRotateFunction,
      self.autoRotateInterval
    );
  }

  cancelAutoRotate() {
    if (!this.autoRotate || !this.autoRotateTimeoutHandle) return;
    clearTimeout(this.autoRotateTimeoutHandle);
    this.autoRotateTimeoutHandle = 0;
  }

  renderArrowButton(title, icon, onClick, isDisabled) {
    const { id } = this.props;

    return (
      <span
        className={`c-carousel__controls  c-carousel__controls--${title.toLowerCase()}`}
      >
        <Button
          color="inline-link"
          onClick={onClick}
          aria-controls={id}
          aria-label={title}
          className="c-carousel__btn"
          isDisabled={isDisabled}
          isInvisible={isDisabled}
        >
          <Icon icon={icon} size="large" title={title}></Icon>
        </Button>
      </span>
    );
  }

  renderIndicators() {
    const { children } = this.props;
    const { page: currentPage } = this.state;

    const indicators = [];
    const self = this;

    for (let page = 0; page < this.state.pageCount; page++) {
      const isCurrentPage = page === currentPage;
      const indicatorClassName = cn({
        'c-carousel__indicator-active': isCurrentPage,
      });

      indicators.push(
        <li
          key={page}
          datapage={page}
          className={indicatorClassName}
          aria-label={'Go to page ' + page}
          onClick={() => {
            self.requestPage(page);
          }}
        ></li>
      );
    }

    return (
      <div className="c-carousel__indicators">
        <ol>{indicators}</ol>
      </div>
    );
  }

  renderSlides(swipeOffset) {
    const { children, slides, carouselOverlap } = this.props;
    const { page, slidesPerPage } = this.state;
    const firstVisibleSlide = page * slidesPerPage;
    const lastVisibleSlide = firstVisibleSlide + slidesPerPage - 1;

    const slidesClassName = cn('o-list-inline', 'c-carousel__slides', {
      'u-transition-none': swipeOffset !== 0,
      ' no-overlap': !carouselOverlap,
    });

    const slideClassName = cn(
      'c-carousel__slide',
      `u-1/${slides['mobile']}`,
      `u-1/${slides['mobileLandscape']}@mobile-landscape`,
      `u-1/${slides['tablet']}@tablet`,
      `u-1/${slides['tabletLandscape']}@tablet-landscape`,
      `u-1/${slides['desktop']}@desktop`
    );

    const slidesStyle = {
      transform: `translateX(-${page * 100}%)`,
    };

    return (
      <ol className={slidesClassName} ref={this.slides} style={slidesStyle}>
        {React.Children.map(children, (child, key) => {
          const isVisible = key >= firstVisibleSlide && key <= lastVisibleSlide;
          const fullSlideClassName = cn(slideClassName, {
            ' is-invisible': !isVisible,
            ' no-overlap': !carouselOverlap,
          });
          return (
            <li
              key={key}
              className={fullSlideClassName}
              aria-hidden={!isVisible}
            >
              {child}
            </li>
          );
        })}
      </ol>
    );
  }

  renderItemCountMessage() {
    const { page, totalItems } = this.state;
    const deviceType = this.getDeviceType(); // Get the device type
    const slidesPerPage =
      deviceType === 'mobile' || deviceType === 'tablet'
        ? this.props.slides.mobile
        : this.props.slides.desktop;
    const start = page * slidesPerPage + 1;
    const end = Math.min(start + slidesPerPage - 1, totalItems);
    const countMessage =
      deviceType === 'mobile' || deviceType === 'tablet'
        ? `Showing ${start} of ${totalItems}`
        : `Showing ${start} - ${end} of ${totalItems}`;
    window.sessionStorage.setItem('carouselCount', countMessage);
    return <div className="c-carousel__item-count f-14">{countMessage}</div>
  }

  render() {
    const { className, id } = this.props;
    const { page, pageCount } = this.state;

    const isFirstPage = page === 0;
    const isLastPage = page === pageCount - 1;

    return (
      <>
        <div className={'recommended_car_container h-28'}>
          <div className={'recommended_car_container_title'}>
            <h4 id={'recomm-access-heading'} className={className}>
              {t('Add to your purchase')}
            </h4>
          </div>
          <div className={'recommended_car_container_count'}>
            {this.renderItemCountMessage()} {/* Render item count message */}
          </div>
        </div>
        <div
          id={id}
          className={cn('c-carousel', className)}
          aria-live="polite"
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          onTouchCancel={this.onTouchEnd}
          onTouchEnd={this.onTouchEnd}
          onTouchMove={this.onTouchMove}
          onTouchStart={this.onTouchStart}
          onTransitionEnd={this.onTransitionEnd}
        >
          {this.renderArrowButton(
            'Previous',
            'arrow-left',
            this.requestPreviousPage,
            isFirstPage
          )}
          {this.renderArrowButton(
            'Next',
            'arrow-right',
            this.requestNextPage,
            isLastPage
          )}
          <div className="c-carousel__viewport">{this.renderSlides(0)}</div>
          {this.props.indicator && this.renderIndicators()}
        </div>
      </>
    );
  }
}

EnhancedCarousel.propTypes = {
  autoRotate: PropTypes.bool,
  autoRotateInterval: PropTypes.number,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  indicator: PropTypes.bool,
  slides: PropTypes.shape({
    mobile: PropTypes.number,
    mobileLandscape: PropTypes.number,
    tablet: PropTypes.number,
    tabletLandscape: PropTypes.number,
    desktop: PropTypes.number,
  }),
  slidesPerPage: PropTypes.number, // New prop for slides per page
};

EnhancedCarousel.defaultProps = {
  autoRotate: false,
  autoRotateInterval: 5000,
  className: '',
  indicator: true,
  slides: {
    mobile: 1,
    mobileLandscape: 1,
    tablet: 1,
    tabletLandscape: 1,
    desktop: 1,
  },
  slidesPerPage: 5, // Default value for slides per page
};
